/// <reference path="ReportViewController.ts" />

// Dashboard v2: a ReportViewController on 0_dashboard_v2.py. The server exposes a
// widget catalog + default layout; the user's UserDashboardItem rows are overlaid
// on the default spec and sent as __dashboard_spec__ so the server renders only
// the widgets the user wants, in their order, sharing one DB connection.
class DashboardViewController extends ReportViewController
{
    // Pseudo report id for per-user dashboard column options (UserReportOption);
    // the dashboard has no Report entity, so this constant takes that role.
    static readonly dashboardReportID = "99000000-0000-2222-9999-000000000001";

    static newInstance() : DashboardViewController {
        let vc = new DashboardViewController("report-detail-view");
        vc.initWithResource("layout/reports/ReportDetailView.html");
        return vc;
    }

    // Widget catalog + default spec, fetched once per session (SQL-free on the server)
    private static catalog:any = null;

    private editButton:MUIButton = null;
    viewDidLoad(){
        super.viewDidLoad();
        this._reportID = DashboardViewController.dashboardReportID;

        this.editButton = MUIOutlet(this, "edit-btn", "MUIButton");
        if (this.editButton != null) {
            MUICoreLayerRemoveStyle(this.editButton.layer, "hidden");
            this.editButton.setAction(this, this.editDashboardAction);
        }

        this.downloadCatalog(null);
    }

    private downloadCatalog(completion:any){
        if (DashboardViewController.catalog != null) {
            if (completion != null) completion.call(this);
            return;
        }

        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let ws = ad.webService as WebService;
        ws.executeReportScriptAtPath("python/0_dashboard_v2.py", {"__dashboard_action__": "catalog"}, this, function(this:DashboardViewController, code:number, data:any){
            if (code != 200 || data == null || data.length == 0) return;
            DashboardViewController.catalog = data[0];
            if (completion != null) completion.call(this);
        });
    }

    private editDashboardAction(button:MUIButton){
        this.downloadCatalog(function(this:DashboardViewController){
            let vc = DashboardEditViewController.newInstance();
            vc.delegate = this;
            vc.setCatalog(DashboardViewController.catalog);
            // navigation inside the popover: widget options push, not popover
            let nc = new MUINavigationController();
            nc.initWithRootViewController(vc);
            AppHelper.sharedInstance().presentPopOverViewController(nc, this.editButton, true);
        });
    }

    applyDashboardOptions(){
        this.reloadData();
    }

    protected additionalFilters():any {
        let spec = this.userDashboardSpec();
        if (spec == null) return null;
        return {"__dashboard_spec__": JSON.stringify(spec)};
    }

    // Builds the spec from the user's UserDashboardItem rows. Each row is one
    // widget INSTANCE — the same widget id may appear several times with
    // different presentations — and together they fully describe the layout,
    // so the spec must NOT depend on the (async) catalog: the first render of
    // a session races it. The catalog, when already fetched, only drops
    // instances of retired widgets. Legacy rows saved by the old show/hide
    // editor may carry hidden=true; they are skipped here and cleaned up by
    // the editor on the next APPLY.
    // Returns null when there are no instances (server renders the default).
    private userDashboardSpec():any {
        let ad = MUIWebApplication.sharedInstance().delegate as AppDelegate;
        let user = ad.selectedUser as User;
        let userItems = user.dashboardItems;
        if (userItems == null || userItems.count == 0) return null;

        let catalog = DashboardViewController.catalog;
        let known = null;
        if (catalog != null) {
            known = {};
            for (let w of catalog["Widgets"] ?? []) known[w["ID"]] = true;
        }

        let items = [];
        for (let ui = 0; ui < userItems.count; ui++){
            let item = userItems.objectAtIndex(ui) as UserDashboardItem;
            if (item.widgetID == null || item.hidden == true) continue;
            if (known != null && known[item.widgetID] != true) continue;
            items.push(item);
        }
        if (items.length == 0) return null;

        items.sort(function(a, b){ return a.orderIndex - b.orderIndex; });

        let rows = [];
        let currentRow = null;
        for (let item of items) {
            if (currentRow == null || item.rowBreak == true) {
                currentRow = {"widgets": []};
                rows.push(currentRow);
            }
            let conf = {"id": item.widgetID};
            if (item.config != null) {
                try {
                    let extra = JSON.parse(item.config);
                    for (let key in extra) conf[key] = extra[key];
                } catch { /* malformed config: render the widget with defaults */ }
            }
            // the server tags rendered items with the instance id, keying
            // column visibility/order per instance instead of per widget
            conf["instance"] = item.identifier;
            currentRow["widgets"].push(conf);
        }
        return {"rows": rows};
    }
}
